import { z } from 'zod';

export const RewardRowSchema = z
  .object({
    id: z.union([z.number(), z.string()]).optional(),
    bonus_name: z.string().min(1, 'Nama bonus/reward wajib diisi').min(3, 'Minimal 3 karakter'),
    category: z.string().min(1, 'Kategori wajib dipilih'),
    org_chart_node_id: z.string().optional().default(''),
    bonus_target: z.string().min(1, 'Syarat pencapaian wajib diisi').min(5, 'Minimal 5 karakter'),
    bonus_amount: z.string().min(1, 'Nominal/jumlah reward wajib diisi'),
    bonus_period: z.string().optional().default(''),
    is_published: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    if (data.category === 'level_jabatan' && !data.org_chart_node_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['org_chart_node_id'],
        message: 'Jabatan wajib dipilih untuk kategori jabatan',
      });
    }
  });

export const RewardFormSchema = z.object({
  items: z.array(RewardRowSchema),
});

export type RewardRowValues = z.infer<typeof RewardRowSchema>;
export type RewardFormValues = z.infer<typeof RewardFormSchema>;

export interface RewardError {
  field: string;
  message: string;
  rowIndex: number;
}

export interface ValidationErrorSummary {
  hasErrors: boolean;
  rowsWithErrors: Map<number, RewardError[]>;
  errorsByRow: Record<number, string[]>;
  errorsByRowAndField: Record<number, Record<string, string>>;
  summary: string;
}

export function validateRewardRow(row: RewardRowValues, index: number): RewardError[] {
  const errors: RewardError[] = [];

  if (!row.bonus_name?.trim()) {
    errors.push({
      field: 'bonus_name',
      message: 'Nama bonus/reward wajib diisi',
      rowIndex: index,
    });
  }

  if (!row.category) {
    errors.push({
      field: 'category',
      message: 'Kategori wajib dipilih',
      rowIndex: index,
    });
  }

  if (!row.bonus_target?.trim()) {
    errors.push({
      field: 'bonus_target',
      message: 'Syarat pencapaian wajib diisi',
      rowIndex: index,
    });
  }

  if (!row.bonus_amount?.trim()) {
    errors.push({
      field: 'bonus_amount',
      message: 'Nominal/jumlah reward wajib diisi',
      rowIndex: index,
    });
  }

  if (row.category === 'level_jabatan' && !row.org_chart_node_id) {
    errors.push({
      field: 'org_chart_node_id',
      message: 'Jabatan wajib dipilih untuk kategori jabatan',
      rowIndex: index,
    });
  }

  return errors;
}

export function formatValidationErrors(items: RewardRowValues[]): ValidationErrorSummary {
  const rowsWithErrors = new Map<number, RewardError[]>();
  const errorsByRow: Record<number, string[]> = {};
  const errorsByRowAndField: Record<number, Record<string, string>> = {};

  items.forEach((item, index) => {
    const rowErrors = validateRewardRow(item, index);
    if (rowErrors.length > 0) {
      rowsWithErrors.set(index, rowErrors);
      errorsByRow[index] = rowErrors.map(e => e.message);
      errorsByRowAndField[index] = Object.fromEntries(rowErrors.map(e => [e.field, e.message]));
    }
  });

  const hasErrors = rowsWithErrors.size > 0;
  let summary = '';

  if (hasErrors) {
    const rowNumbers = Array.from(rowsWithErrors.keys())
      .map(i => i + 1)
      .join(', ');
    summary = `${rowsWithErrors.size} baris memiliki kesalahan (Baris: ${rowNumbers})`;
  }

  return {
    hasErrors,
    rowsWithErrors,
    errorsByRow,
    errorsByRowAndField,
    summary,
  };
}
